import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '../../store/authStore';
import Button from '../../components/Button';
import { Shield } from 'lucide-react';
import OnboardingProgress from '../../components/OnboardingProgress';

const schema = z.object({
  acceptTerms: z.boolean().refine((val) => val === true, {
    message: 'You must accept the terms to continue',
  }),
});

type FormData = z.infer<typeof schema>;

export default function OnboardingTerms() {
  const navigate = useNavigate();
  const { user, updateProfile } = useAuthStore();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      acceptTerms: false,
    },
  });

  // Check if terms are already accepted
  React.useEffect(() => {
    if (user?.terms_accepted) {
      // If terms are already accepted, update local storage and redirect
      localStorage.setItem('onboardingStatus', 'complete');
      
      // Get redirect path if any
      const redirectTo = sessionStorage.getItem('authRedirect') || '/';
      sessionStorage.removeItem('authRedirect');
      
      // Use replace to prevent back navigation to onboarding
      navigate(redirectTo, { replace: true });
    }
  }, [user, navigate]);

  const onSubmit = async (data: FormData) => {
    try {
      setIsSubmitting(true);
      setError(null);

      // Update profile with terms acceptance AND onboarding completion
      await updateProfile({
        terms_accepted: true,
        terms_accepted_at: new Date().toISOString(),
        onboarding_completed: true
      });

      // Update local storage
      localStorage.setItem('onboardingStatus', 'complete');

      // Clear any stored onboarding step
      localStorage.removeItem('onboardingStep');
      sessionStorage.removeItem('onboardingStep');

      // Get redirect path if any
      const redirectTo = sessionStorage.getItem('authRedirect') || '/';
      sessionStorage.removeItem('authRedirect');

      // Use replace to prevent back navigation to onboarding
      navigate(redirectTo, { replace: true });
    } catch (error: any) {
      console.error('Failed to complete onboarding:', error);
      setError(error.message || 'Failed to complete onboarding. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-light px-4 py-8">
      <OnboardingProgress currentStep="terms" />
      <div className="max-w-md mx-auto">
        <div className="flex items-center justify-center mb-8">
          <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center">
            <Shield className="w-8 h-8 text-surface-light" />
          </div>
        </div>

        <h1 className="text-3xl font-bold font-dillan text-primary mb-2 text-center">
          Before we begin
        </h1>
        <p className="text-neutral text-l mb-6 text-center">
          Please review our community guidelines
        </p>

        <div className="bg-surface bg-opacity-80 rounded-xl p-6 mb-4">
          
          <div className="space-y-4">
            <div>
              <h3 className="font-dillan font-bold text-neutral">Professional Conduct</h3>
              <p className="text-sm text-neutral">We value honesty, fairness, and transparency. Be truthful in your profile, act professionally, and respect others on the platform.</p>
            </div>

            <div>
              <h3 className="font-dillan font-bold text-neutral">Platform Safety</h3>
              <p className="text-sm text-neutral">We take security and feedback seriously. All content and users are monitored to maintain a safe, trustworthy environment.</p>
            </div>

            <div>
              <h3 className="font-dillan font-bold text-neutral">Platform Rules</h3>
              <p className="text-sm text-neutral">We foster an inclusive, respectful community. Hate speech, harassment, and discrimination are not tolerated.</p>
            </div>

            <div>
              <h3 className="font-dillan font-bold text-neutral">Privacy Policy</h3>
              <p className="text-sm text-neutral">Your personal data is stored securely and never sold. We use your information only to operate the platform and improve your experience. You can view, update, download, or delete your data at any time in your profile settings.</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {error && (
            <div className="bg-secondary-dark bg-opacity-10 text-secondary-dark p-4 rounded-lg">
              {error}
            </div>
          )}

          <label className="flex items-start space-x-3">
            <input
              type="checkbox"
              {...register('acceptTerms')}
              className="mt-1 w-4 h-4 pl-2 border-2 border-neutral rounded-full checked:bg-neutral checked:border-neutral focus:ring-accent appearance-none transition-all"

            />
            <span className="text-sm text-neutral">
              I agree to maintain a respectful, professional environment and follow the community guidelines
            </span>
          </label>

          {errors.acceptTerms && (
            <p className="text-sm text-primary">{errors.acceptTerms.message}</p>
          )}

          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1 text-surface-light mt-4"
              onClick={() => navigate('/onboarding/goals')}
              disabled={isSubmitting}
            >
              Back
            </Button>
            <Button 
              type="submit" 
              variant="primary" 
              className="flex-1 text-surface-light mt-4"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Completing...' : 'Complete Setup'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}